/**
 * cart-benefits.js — Auto-adds "Extended 2-Year Warranty" and "Free Shipping &
 * Protection" when a qualifying (non-benefit) product is in the cart, and
 * removes them when none remain. Ensures each benefit appears at most once.
 *
 * AU store: free delivery on all orders — no minimum threshold.
 *
 * Variant IDs (stable, single-variant products):
 *   Free Shipping & Protection  – 67799137353927
 *   Extended 2-Year Warranty     – 67799137452231
 */
(function () {
  'use strict';

  var BENEFIT_VARIANT_IDS = ['67799137353927', '67799137452231'];
  var _benefitSet = {};
  BENEFIT_VARIANT_IDS.forEach(function (id) { _benefitSet[id] = true; });

  /* ── Helpers ── */

  function isBenefit(variantId) {
    return _benefitSet[String(variantId)] === true;
  }

  function fetchCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  function hasQualifyingProducts(cartItems) {
    for (var i = 0; i < cartItems.length; i++) {
      if (!isBenefit(cartItems[i].variant_id)) return true;
    }
    return false;
  }

  /* ── Pre-add filtering ──
   * Given an array of {id, quantity} items about to be POSTed to /cart/add,
   * returns a filtered copy that omits benefits already in the cart and
   * deduplicates benefits within the array itself.
   */
  function filterBenefitsFromItems(items) {
    return fetchCart().then(function (cart) {
      var benefitsInCart = {};
      cart.items.forEach(function (item) {
        if (isBenefit(item.variant_id)) {
          benefitsInCart[String(item.variant_id)] = true;
        }
      });

      var benefitsSeen = {};
      return items.filter(function (item) {
        var vid = String(item.id);
        if (isBenefit(vid)) {
          if (benefitsInCart[vid] || benefitsSeen[vid]) return false;
          benefitsSeen[vid] = true;
        }
        return true;
      });
    });
  }

  /* ── Post-mutation normalization ──
   * Fetches current cart, fixes any duplicate benefit lines or qty > 1,
   * removes benefits when no qualifying product remains.
   * Returns a Promise that resolves with the /cart/update.js response
   * (including section HTML) or null if no fix was needed.
   */
  var _normalizing = false;

  function normalize() {
    if (_normalizing) return Promise.resolve(null);
    _normalizing = true;

    return fetchCart().then(function (cart) {
      var updates = {};
      var needsUpdate = false;
      var qualifying = hasQualifyingProducts(cart.items);

      // Collect which benefits are already in the cart
      var benefitsInCart = {};
      BENEFIT_VARIANT_IDS.forEach(function (vid) {
        var lines = [];
        cart.items.forEach(function (item) {
          if (String(item.variant_id) === vid) {
            lines.push({ key: item.key, quantity: item.quantity });
          }
        });
        benefitsInCart[vid] = lines;
      });

      // Auto-add missing benefits when qualifying products exist
      var itemsToAdd = [];
      if (qualifying) {
        BENEFIT_VARIANT_IDS.forEach(function (vid) {
          if (benefitsInCart[vid].length === 0) {
            itemsToAdd.push({ id: parseInt(vid, 10), quantity: 1 });
          }
        });
      }

      // Fix duplicates, cap qty, or remove benefits
      BENEFIT_VARIANT_IDS.forEach(function (vid) {
        var lines = benefitsInCart[vid];
        if (lines.length === 0) return;

        if (!qualifying) {
          // No qualifying products → remove all benefit lines
          lines.forEach(function (line) {
            updates[line.key] = 0;
            needsUpdate = true;
          });
        } else if (lines.length > 1) {
          // Multiple lines for the same benefit → keep first at 1, remove rest
          lines.forEach(function (line, i) {
            if (i === 0) {
              if (line.quantity !== 1) {
                updates[line.key] = 1;
                needsUpdate = true;
              }
            } else {
              updates[line.key] = 0;
              needsUpdate = true;
            }
          });
        } else if (lines[0].quantity > 1) {
          // Single line but qty > 1 → cap at 1
          updates[lines[0].key] = 1;
          needsUpdate = true;
        }
      });

      // Build section list for re-rendering
      var sections = ['cart-icon-bubble'];
      var cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        var sid = cartDrawer.getAttribute('data-section-id');
        if (sid && sections.indexOf(sid) === -1) sections.push(sid);
      }
      var mainCart = document.querySelector('.main-cart');
      if (mainCart) {
        var mid = mainCart.getAttribute('data-section-id');
        if (mid && sections.indexOf(mid) === -1) sections.push(mid);
      }

      // Add missing benefit items first, then apply fixes
      var addPromise = (itemsToAdd.length > 0)
        ? fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: itemsToAdd })
          }).then(function (r) { return r.json(); })
        : Promise.resolve(null);

      return addPromise.then(function () {
        if (!needsUpdate && itemsToAdd.length === 0) {
          _normalizing = false;
          return null;
        }

        if (!needsUpdate && itemsToAdd.length > 0) {
          // Only added items, no updates needed — just re-fetch sections
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              updates: {},
              sections: sections,
              sections_url: window.location.pathname
            })
          })
          .then(function (r) { return r.json(); })
          .then(function (response) {
            _normalizing = false;
            rerenderCart(response);
            return response;
          });
        }

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates: updates,
            sections: sections,
            sections_url: window.location.pathname
          })
        })
        .then(function (r) { return r.json(); })
        .then(function (response) {
          _normalizing = false;
          rerenderCart(response);
          return response;
        });
      });
    }).catch(function (e) {
      _normalizing = false;
      console.error('CartBenefits: normalize error', e);
      return null;
    });
  }

  /* ── Cart UI re-rendering ── */

  function rerenderCart(response) {
    if (!response || !response.sections) return;

    // Cart drawer
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents(response);
    }

    // Main cart page
    var mainCart = document.querySelector('main-cart');
    if (mainCart && typeof mainCart.renderContents === 'function') {
      mainCart.renderContents(response);
    }
  }

  /* ── Debounced normalizer triggered after cart mutations ── */

  var _debounceTimer = null;

  function scheduleNormalize() {
    if (_normalizing) return;
    clearTimeout(_debounceTimer);
    _debounceTimer = setTimeout(function () {
      normalize();
    }, 300);
  }

  /* ── Listen for cart mutation events ── */

  // Fires after bundle-contents.js and module-add-to-cart.js adds
  document.addEventListener('nextsky:cart:added', function (e) {
    if (e.detail && e.detail.success) scheduleNormalize();
  });

  // Intercept fetch to /cart/change.js and /cart/add.js so we normalize
  // after item removal, quantity updates, or new items added
  var _origFetch = window.fetch;
  window.fetch = function () {
    var url = arguments[0];
    var urlStr = (typeof url === 'string') ? url : (url && url.url ? url.url : '');
    var isCartMutation = !_normalizing && (urlStr.indexOf('/cart/change') !== -1 || urlStr.indexOf('/cart/add') !== -1);
    var result = _origFetch.apply(this, arguments);
    if (isCartMutation) {
      result.then(function () { scheduleNormalize(); });
    }
    return result;
  };

  // Normalize on page load (fix any pre-existing duplicates)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(normalize, 500);
    });
  } else {
    setTimeout(normalize, 500);
  }

  /* ── Public API ── */

  window.CartBenefits = {
    BENEFIT_VARIANT_IDS: BENEFIT_VARIANT_IDS,
    isBenefit: isBenefit,
    filterBenefitsFromItems: filterBenefitsFromItems,
    normalize: normalize,
    scheduleNormalize: scheduleNormalize
  };
})();
